import { Directive, inject, WritableSignal } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";

import { FormComponent } from "@app/shared/models/form.model";

import {
  FieldErrorConfig,
  FormValidationResult,
} from "./form-validation.types";
import { setupFormValidation } from "./form-validation.utils";

/**
 * Abstract base class for form components that provides common validation functionality.
 *
 * Subclasses must:
 * 1. Define `form` property with their specific FormGroup
 * 2. Call `initializeValidation()` in constructor after form is created
 * 3. Define `fieldErrorConfigs` to specify error messages for each field
 *
 * @example
 * ```typescript
 * @Component({...})
 * export class MyFormComponent extends BaseFormComponent<MyFormValue> {
 *   protected readonly fieldErrorConfigs: FieldErrorConfig[] = [
 *     {
 *       controlName: 'name',
 *       errorMessages: {
 *         required: 'Name is required',
 *       },
 *     },
 *   ];
 *
 *   readonly form = this.fb.group({
 *     name: this.fb.nonNullable.control('', [Validators.required]),
 *   });
 *
 *   constructor() {
 *     super();
 *     this.initializeValidation();
 *   }
 * }
 * ```
 */
@Directive()
export abstract class BaseFormComponent<T> implements FormComponent<T> {
  protected readonly fb = inject(FormBuilder);

  /** The form group - must be defined by subclass */
  abstract readonly form: FormGroup;

  /** Field error configurations - must be defined by subclass */
  protected abstract readonly fieldErrorConfigs: FieldErrorConfig[];

  /** Validation result containing error states and utilities */
  protected validation!: FormValidationResult;

  /**
   * Initialize validation listeners.
   * Must be called from constructor after form is created.
   */
  protected initializeValidation(): void {
    this.validation = setupFormValidation({
      form: this.form,
      fieldConfigs: this.fieldErrorConfigs,
    });
  }

  /**
   * Get the error message signal for a field.
   * Use in templates: `{{ errorMessage('fieldName')() }}`
   */
  errorMessage(controlName: string): WritableSignal<string> {
    const signal = this.validation.getErrorMessage(controlName);
    if (!signal) {
      throw new Error(
        `No error message signal found for control: ${controlName}`,
      );
    }
    return signal;
  }

  /**
   * Get the show error signal for a field.
   * Use in templates: `@if (showError('fieldName')()) { ... }`
   */
  showError(controlName: string): WritableSignal<boolean> {
    const signal = this.validation.getShowError(controlName);
    if (!signal) {
      throw new Error(`No show error signal found for control: ${controlName}`);
    }
    return signal;
  }

  /**
   * Validate the form. Returns true if valid, false otherwise.
   * Shows error messages for invalid fields.
   */
  validate(): boolean {
    return this.validation.validate();
  }

  /**
   * Mark all form controls as touched and update error messages.
   */
  markAllAsTouched(): void {
    this.validation.markAllAsTouched();
  }

  /**
   * Get the current form value.
   */
  getFormValue(): T {
    return this.form.getRawValue() as T;
  }

  /**
   * Reset the form and clear all error states.
   */
  reset(): void {
    this.form.reset();
    this.validation.resetErrors();
  }
}
